import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RouterOutlet } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TourService as MDTourService, TourStepTemplateComponent } from 'ngx-ui-tour-md-menu';
import { combineLatest, EMPTY, finalize, switchMap, tap, timer } from 'rxjs';
import { DialogService } from './components/dialog/dialog.service';
import { IsMarketingDialogComponent } from './components/dialog/is-marketing-dialog/is-marketing-dialog.component';
import { MdiIconComponent } from './components/mdi-icon/mdi-icon.component';
import { UserCookiesConsentComponent } from './components/user-cookies-consent/user-cookies-consent.component';
import { DIALOG_DIMENSIONS_L_M } from './const/dialog/dialog.const';
import { BackgroundAudioPlayerService } from './services/audio/background-audio-player.service';
import { AuthService } from './services/auth/auth.service';
import { CompanyService } from './services/company/company.service';
import { MessageService } from './services/message/message.service';
import { SettingsService } from './services/settings/settings.service';
import { TourService } from './services/tour/tour.service';
import { AiChatService } from './services/ai-chat/ai-chat';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        RouterOutlet,
        UserCookiesConsentComponent,
        TourStepTemplateComponent,
        NgIf,
        MatProgressSpinner,
        MdiIconComponent,
        MatButtonModule,
        TranslateModule,
    ],
})
export class AppComponent implements OnInit {
    private readonly settingsService = inject(SettingsService);
    private readonly tourService = inject(TourService);
    private readonly messageService = inject(MessageService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly authService = inject(AuthService);
    private readonly companyService = inject(CompanyService);
    private readonly _backgroundAudioPlayerService = inject(BackgroundAudioPlayerService);
    protected readonly mdTourService = inject(MDTourService);
    private readonly dialogService = inject(DialogService);
    private readonly aiChatService = inject(AiChatService);

    readonly tokenSwapLoading = this.authService.loading;

    ngOnInit(): void {
        this.initTourService();
        this.authService.initialize();
        this.getMessages();
        this.resolveIsMarketingFlag();
        this.initChatBot();
    }

    private initChatBot(): void {
        this.authService.user$
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap((user) => {
                    if (user) {
                        this.aiChatService.initChatBot();
                    } else {
                        this.aiChatService.destroyChatBot();
                    }
                })
            )
            .subscribe();
    }

    private getMessages(): void {
        combineLatest([this.authService.user$, this.companyService.activeCompany$])
            .pipe(
                switchMap(([user, company]) =>
                    user && company
                        ? timer(0, 5 * 60 * 1000).pipe(switchMap(() => this.messageService.getMessages()))
                        : EMPTY
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private resolveIsMarketingFlag(): void {
        this.authService.user$
            .pipe(
                switchMap((user) => {
                    if (!user || (user.isMarketing !== null && user.isMarketing !== undefined)) return EMPTY;
                    const modalRef = this.dialogService.modal({
                        component: IsMarketingDialogComponent,
                        ...DIALOG_DIMENSIONS_L_M,
                    });

                    return modalRef.afterClosed().pipe(
                        switchMap((isMarketing: boolean) => this.authService.updateIsMarketingFlag(isMarketing)),
                        finalize(() => modalRef.close())
                    );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private initTourService() {
        this.settingsService.init().then(async () => {
            await this.tourService.init();
        });
    }
}
